import React from 'react';
import { Spinner } from 'reactstrap';

// Round DB-1 (2026) — shop-floor board restyle (owner: "color code is not
// [visible]..not able to see properly..change the UI").
//
// The old table was a generic small-font bootstrap table. Display boards are
// read from across a shop floor on TVs, so this brings back the legacy
// WinForms board look with modern polish:
//   • Dark-navy header, white text (legacy rgb(20,25,72)) — high contrast.
//   • Alternating row shading (legacy rgb(238,239,249)) that's actually
//     visible.
//   • Bigger base font (15px) and roomier cells.
//   • ROW STATUS COLOR CODING (new, via the `highlightKey` prop):
//       green left-bar + tinted row  = employee has an ACTIVE job right now
//       plain/dimmed row             = clocked in but idle / no active job
//     A small legend below the table explains the colors.
//   • API is backward-compatible: columns/rows/loading/emptyMessage/
//     onRowClick all behave exactly as before; `highlightKey` is optional.
const NAVY = '#141948';
const STRIPE = '#eeeff9';
const ACTIVE_BG = '#e7f6ec';
const ACTIVE_BAR = '#2ca24f';

const DataTable = ({
    columns, rows, loading,
    emptyMessage = 'No records found',
    onRowClick,
    highlightKey,          // e.g. 'JobNum' — row is "active" when non-empty
}) => {
    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="text-muted mt-2 mb-0">Loading data...</p>
            </div>
        );
    }

    if (!rows || rows.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="ri-inbox-line" style={{ fontSize: 36, color: '#adb5bd' }}></i>
                <p className="text-muted mt-2 mb-0">{emptyMessage}</p>
            </div>
        );
    }

    const isActive = (row) => {
        if (!highlightKey) return false;
        const v = row[highlightKey];
        return v !== null && v !== undefined && String(v).trim() !== '';
    };
    const activeCount = highlightKey ? rows.filter(isActive).length : 0;

    return (
        <div className="table-responsive">
            <table className="align-middle mb-0" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {columns.map((col, idx) => (
                            <th
                                key={idx}
                                className="text-nowrap"
                                style={{
                                    background: NAVY,
                                    color: '#ffffff',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    letterSpacing: '0.4px',
                                    textTransform: 'uppercase',
                                    padding: '12px 14px',
                                    borderBottom: `3px solid ${ACTIVE_BAR}`,
                                    position: 'sticky',
                                    top: 0,
                                    ...(col.style || {}),
                                }}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rIdx) => {
                        const active = isActive(row);
                        const rowBg = active
                            ? ACTIVE_BG
                            : (rIdx % 2 === 1 ? STRIPE : '#ffffff');
                        return (
                            <tr
                                key={rIdx}
                                style={{
                                    background: rowBg,
                                    ...(onRowClick ? { cursor: 'pointer' } : {}),
                                }}
                                onClick={() => onRowClick && onRowClick(row)}
                            >
                                {columns.map((col, cIdx) => {
                                    const val = row[col.key];
                                    return (
                                        <td
                                            key={cIdx}
                                            className="text-nowrap"
                                            style={{
                                                fontSize: 15,
                                                padding: '10px 14px',
                                                color: active ? '#1c2333' : '#5b6478',
                                                fontWeight: active && cIdx === 0 ? 700 : 500,
                                                borderBottom: '1px solid #e4e7f0',
                                                // Green status bar on the first cell of active rows
                                                ...(highlightKey && cIdx === 0
                                                    ? { borderLeft: `5px solid ${active ? ACTIVE_BAR : 'transparent'}` }
                                                    : {}),
                                                ...(col.cellStyle || {}),
                                            }}
                                        >
                                            {col.render ? col.render(val, row) : (val ?? '—')}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Color legend — only when status coloring is on */}
            {highlightKey && (
                <div
                    className="d-flex align-items-center gap-3 flex-wrap"
                    style={{ padding: '8px 14px', fontSize: 12.5, color: '#5b6478', borderTop: '1px solid #e4e7f0', background: '#fafbfe' }}
                >
                    <span>
                        <span style={{ display: 'inline-block', width: 12, height: 12, background: ACTIVE_BG, border: `2px solid ${ACTIVE_BAR}`, borderRadius: 3, marginRight: 6, verticalAlign: '-2px' }} />
                        On an active job ({activeCount})
                    </span>
                    <span>
                        <span style={{ display: 'inline-block', width: 12, height: 12, background: STRIPE, border: '1px solid #cfd4e4', borderRadius: 3, marginRight: 6, verticalAlign: '-2px' }} />
                        No active job ({rows.length - activeCount})
                    </span>
                    <span className="ms-auto">{rows.length} employees</span>
                </div>
            )}
        </div>
    );
};

export default DataTable;
